import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Alert, Empty, InputNumber, Modal, Table } from 'antd';
import _ from 'lodash';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import ListHeader from '../../../../components/ListHeader';
import { Feature, ProductVersionModel } from '../../../../models/product-interface';
import { Profile } from '../../../../models/profile-interface';
import { CustomerServiceQueries } from '../../../../queries/customer-service-queries';
import { ProductQueries } from '../../../../queries/product-queries';
import AddFeaturesModal from '../../../../components/modals/AddFeatureModal';

type Filter = {
  text: string;
  value: number | string;
}

type Props = {
    profile: Profile;
    product: ProductVersionModel;
    visible: boolean;
    isDrafting: boolean;
    draftFeatureList: Feature[];
    setDraftFeatureList: React.Dispatch<React.SetStateAction<Feature[]>>
  }
  
  const ProductFeatures: React.FC<Props> = ({ 
    profile,
    product,
    visible,
    isDrafting,
    draftFeatureList,
    setDraftFeatureList
  }) => {

    const history = useHistory();
    const featuresQuery = ProductQueries.useFeaturesList();
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
    const [filteredData, setFilteredData] = useState<Feature[]>(null);
    const [filterString, setFilterString] = useState<string | null>(null);
    const [addFeaturesVisible, setAddFeaturesVisible] = useState<bool>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const addProductFeaturesMutator = ProductQueries.useAddProductFeatures(product.product.id, product.versionId)
    const deleteProductFeaturesMutator = ProductQueries.useDeleteProductFeatures(product.product.id, product.versionId)

    useEffect(() => {
      if(!isDrafting) {
        const features = product?.features ?? []
        features.forEach(x => x.key = x.id) 
        setFilteredData(product.features)
      }
    }, [product, isDrafting]);

    useEffect(() => {
      if(isDrafting) {
        setDraftFeatureList(_.cloneDeep(product.features));
      }
    }, [isDrafting])

    useEffect(() => {
      let searchedData = isDrafting ? draftFeatureList : product.features
      if(!!filterString) {
        searchedData = searchedData.filter(o => o.name.toLowerCase().includes(filterString.toLowerCase()));
      }

      setFilteredData(searchedData);
    }, [isDrafting, filterString, draftFeatureList])

    
  const columns: any = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Feature) => {
        return (name
        )
      },
      sorter: {
        compare: (a: Feature, b: Feature) =>
          a.name.localeCompare(b.name)
      },
    },
    {
      title: 'Record Limit',
      dataIndex: 'recordLimit',
      key: 'recordLimit',
      render: (recordLimit: number, record: Feature) => {
        if(isDrafting && record.allowLimit) {
          return <InputNumber
            value={record.recordLimit}
            onChange={(val) => {
              var newList = _.cloneDeep(draftFeatureList);
              var feature = newList.find(x => x.id == record.id) as Feature;
              feature.recordLimit = val ?? null;
              setDraftFeatureList(newList);
            }}
            min={0}
          />
        }
        
        return recordLimit > 0 ? recordLimit : "None"
      },
      sorter: {
        compare: (a: Feature, b: Feature) =>
          a.recordLimit == b.recordLimit ? 0 : a.recordLimit > b.recordLimit ? -1 : 1
      },
    },
  ];

    const handleRemoveFeatures = () => {
      if(isDrafting) {
        var list = _.cloneDeep(draftFeatureList).filter(x => !selectedRowKeys.includes(x.id))
        setDraftFeatureList(list);
        setSelectedRowKeys([]);
      } else {
        Modal.confirm({
          title: `Are you sure you want to remove ${selectedRowKeys.length > 1 ? 'these features' : 'this feature'} from the product?`,
          onOk: () => {
            var list = _.cloneDeep(product.features).filter(x => selectedRowKeys.includes(x.id))
            deleteProductFeaturesMutator.mutateAsync(list).finally(() => {
              setSelectedRowKeys([]);
            });
          }
        })
      }
    }
    
    /**
    * On selection changes
    * @param selectedRowKeys
    */
     const onSelectChange = (selectedRowKeys) => {
        setSelectedRowKeys(selectedRowKeys);
        // setValue(value + 1);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange
    };

    /**
     * Unselect all rows
     */
    const handleUnselectAll = () => {
        setSelectedRowKeys([]);
    }

    const handleAddFeaturesToCurrentProduct = (featureList: Feature[]) => {
      setIsLoading(true);
      if(isDrafting) {
        setDraftFeatureList([..._.cloneDeep(draftFeatureList), ...featureList]);
        setAddFeaturesVisible(false);
        setIsLoading(false);
      } else {
        addProductFeaturesMutator.mutateAsync(featureList).then(() => {
          setAddFeaturesVisible(false);
        }).finally(() => {
          setIsLoading(false);
        })
      }
    }

    /**
     * Actions for header
     */
     const actions = [
        {
            singleName: "Add Features",
            multiName: "Add Features",
            multiSelect: false,
            alwaysEnabled: selectedRowKeys.length === 0,
            icon: faPlus,
            tag: "AddFeatures",
            forceDisable: selectedRowKeys.length > 0,
            onClick: () => {
                setAddFeaturesVisible(true);
            }
        },{
            singleName: "Remove Features",
            multiName: "Remove Features",
            multiSelect: true,
            icon: faMinus,
            tag: "RemoveFeatures",
            forceDisable: (product?.companies?.length > 0 && !isDrafting) || !(selectedRowKeys.length > 0),
            onClick: () => {
              handleRemoveFeatures();
            }
        },
    ];

  return <React.Fragment key={"features"}>
    {!isDrafting && product?.companies.length > 0 && <><Alert showIcon message="Features cannot be removed from product versions actively used by companies."></Alert><br/></>}
  <ListHeader 
                    actions={actions}
                    tag="productFeatures"
                    selectedItems={selectedRowKeys?.length ?? 0}
                    totalItems={product.features.length}
                    searchPlaceholder="Search features"
                    unselectAll={handleUnselectAll}
                    onSearch={setFilterString}
                    reset={visible}
                />
  <Table
    columns={columns}
    rowSelection={isDrafting || product?.companies.length === 0 ? rowSelection : null}
    rowKey={(val) => val.id}
    dataSource={filteredData}
    size="middle"
    locale={{
      emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={`No features set`} />
    }}
    scroll={{ x: 715 }}
    pagination={{defaultPageSize: 50}}
  />
    <AddFeaturesModal
      isLoading={isLoading || featuresQuery.isLoading}
      currentFeatures={isDrafting ? draftFeatureList : product.features}
      allFeatures={featuresQuery.data}
      setIsModalVisible={setAddFeaturesVisible}
      isModalVisible={addFeaturesVisible}
      setIsLoading={setIsLoading}
      handleAddFeatures={handleAddFeaturesToCurrentProduct}
    />
  </React.Fragment>
}


export default ProductFeatures;